/**
 * auth-store.ts
 *
 * Zustand store for auth state. Replaces React useState in AuthContext.
 *
 * Key properties:
 * - `initialized` starts true when a cached user exists, otherwise it is
 *   set true after the first Supabase session check completes.
 * - `loading` is only true during active sign-in / sign-up / sign-out flows.
 * - State is seeded synchronously from the cookie cache before the first
 *   render so navigating between pages never shows a loading screen.
 * - IMPORTANT: Session tokens are NOT stored in localStorage for security.
 *   Only user and profile data are cached. The session is always fetched
 *   fresh from httpOnly cookies on mount.
 */

import { create } from 'zustand';
import type { User, Session } from '@supabase/auth-js';

interface Profile {
  id: string;
  email: string;
  role: string;
  full_name: string | null;
  avatar_url: string | null;
  banned_until: string | null;
  created_at: string;
  last_sign_in_at: string | null;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  /** True only during explicit auth actions (signIn, signOut, signUp). */
  loading: boolean;
  /** True once the initial getSession() call has resolved. */
  initialized: boolean;

  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  reset: () => void;
}

const CACHE_KEY = 'menura-auth-cache';

/** Read the cache synchronously , used to seed state before first render. */
function readCache(): { user: User | null; profile: Profile | null } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Strip session tokens from cache , only user and profile are stored
    return {
      user: parsed.user || null,
      profile: parsed.profile || null,
    };
  } catch {
    return null;
  }
}

function writeCache(data: { user: User | null; profile: Profile | null }) {
  if (typeof window === 'undefined') return;
  try {
    // Only cache user and profile , NEVER cache session tokens in localStorage
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      user: data.user,
      profile: data.profile,
    }));
  } catch (e) {
    console.warn('[auth-store] Failed to write auth cache:', e);
  }
}

function clearCache() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (e) {
    console.warn('[auth-store] Failed to clear auth cache:', e);
  }
}

// Seed from cache synchronously so the very first render already has data.
const cached = readCache();

export const useAuthStore = create<AuthState>((set) => ({
  user: cached?.user ?? null,
  session: null, // Session is NEVER cached in localStorage , always fetched fresh from cookies
  profile: cached?.profile ?? null,
  // If we had a cached user, we optimistically treat them as known
  // and skip the initial loading screen. The background getSession() will
  // validate and correct this silently.
  loading: false,
  initialized: Boolean(cached?.user),

  setUser: (user) => set({ user }),
  setSession: (session) => set({ session }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),
  setInitialized: (initialized) => set({ initialized }),
  reset: () => {
    clearCache();
    set({ user: null, session: null, profile: null, loading: false, initialized: true });
  },
}));

export { readCache, writeCache, clearCache, CACHE_KEY };
export type { Profile };
